<div class="pdf-form-order-container">
  <!-- Loading Overlay -->
  @if (isSaving) {
<div class="loader-overlay">
    <div class="backdrop"></div>
    <ap-loading class="loader" [loadingType]="'circular'" [indeterminate]="true" [compact]="false"></ap-loading>
  </div>
}

  <!-- Filter Toolbar -->
  <div class="top-toolbar">
    <div class="float-left">
      <div class="dropdown-with-loader">
        <ap-dropdown class="tax-year-dropdown" [list]="taxYearItems" [selectType]="'single'"
          [title]="'Tax Year'" [hideTitleOnInput]="true" [inputAriaLabel]="'Tax Year'"
          [disabled]="loading || isSaving || loadingTaxYears"
          [(ngModel)]="selectedTaxYearItem" (onSelect)="onTaxYearDropdownChange($event)">
          @for (item of taxYearItems; track $index) {
            <ap-dropdown-list-item #dropdownListItem [item]="item"
              [ariaLabel]="setTaxYearAriaLabel(item)"
              (onSelectItem)="onTaxYearSelectItem($event)"></ap-dropdown-list-item>
          }
        </ap-dropdown>
        @if (loadingTaxYears) {
<span class="inline-loader">
          <ap-loading [loadingType]="'circular'" [indeterminate]="true" [compact]="true"></ap-loading>
        </span>
}
      </div>

      <div class="dropdown-with-loader">
        <ap-dropdown class="return-type-dropdown" [list]="returnTypeItems" [selectType]="'single'"
          [title]="'Return Type'" [hideTitleOnInput]="true" [inputAriaLabel]="'Return Type'"
          [disabled]="loading || isSaving || loadingReturnTypes"
          [(ngModel)]="selectedReturnTypeItem" (onSelect)="onReturnTypeDropdownChange($event)">
          @for (item of returnTypeItems; track $index) {
            <ap-dropdown-list-item #dropdownListItem [item]="item"
              [ariaLabel]="setDropdownAriaLabel(item, 'Return Type')"
              (onSelectItem)="onReturnTypeSelectItem($event)"></ap-dropdown-list-item>
          }
        </ap-dropdown>
        @if (loadingReturnTypes) {
<span class="inline-loader">
          <ap-loading [loadingType]="'circular'" [indeterminate]="true" [compact]="true"></ap-loading>
        </span>
}
      </div>

      <ap-button 
        [label]="'Filter'" 
        [btnType]="'primary'" 
        [compact]="true"
        [disabled]="loading || isSaving || loadingTaxYears || loadingReturnTypes"
        (onClick)="onFilterClick()">
      </ap-button>
    </div>

    <div class="float-right">
      <ap-button 
        [label]="'Sort A-Z'" 
        [btnType]="'primary'" 
        [compact]="true"
        [disabled]="loading || isSaving || pdfForms.length === 0"
        (onClick)="onSortAscending()">
      </ap-button>

      <ap-button 
        [label]="'Sort Z-A'" 
        [btnType]="'primary'" 
        [compact]="true"
        [disabled]="loading || isSaving || pdfForms.length === 0"
        (onClick)="onSortDescending()">
      </ap-button>

      <span class="separator">|</span>

      <ap-button 
        [label]="'Save'" 
        [btnType]="'primary'" 
        [compact]="true"
        [disabled]="!hasUnsavedChanges || loading || isSaving"
        (onClick)="onSaveClick()">
      </ap-button>
    </div>
  </div>

  <!-- Unsaved Changes Warning -->
  @if (hasUnsavedChanges) {
<div class="unsaved-changes-warning">
    <i class="fa fa-exclamation-triangle"></i>
    <span>You have unsaved changes. Click "Save" to preserve your new order.</span>
  </div>
}

  <!-- PDF Forms reorder list
       Scroll lives ONLY on `.orderlist-list`, which flexes into whatever
       height the toolbar and the warning banner leave behind (see the
       component SCSS). The host clips at `calc(100vh - 115px)`, so the
       column can never push the workspace into an outer scrollbar. -->
  <div class="orderlist-container" [class.disabled]="loading || isSaving">
    <div class="orderlist">
      <div class="orderlist-list-container">
        <div class="orderlist-filter-container">
          <ap-search
            [searchType]="'secondary'"
            [placeholder]="'Search'"
            [ariaLabel]="'Search PDF forms'"
            [(ngModel)]="filterTerm"
            (ngModelChange)="refreshVisibleForms()">
          </ap-search>
        </div>

        <ap-list
          #reorderList
          class="orderlist-list"
          [bordered]="false"
          [role]="'listbox'"
          [attr.aria-multiselectable]="'true'"
          [dragStatus]="dragStatus"
          cdkDropList
          (cdkDropListDropped)="onDrop($event)">
          @for (form of visibleForms; track form) {
<ap-list-item
            cdkDrag
            [class.selected]="isSelected(form)"
            [attr.role]="'option'"
            [attr.aria-selected]="isSelected(form)"
            [attr.aria-label]="form.FORM"
            (click)="onItemClick($event, form)"
            (keyup.enter)="onItemClick($event, form)">
            <div class="form-item">
              <span class="form-name">{{ form.FORM }}</span>
              <span class="form-order">{{ form.FORM_ORDER }}</span>
            </div>
          </ap-list-item>
}
          @if (visibleForms.length === 0) {
<div class="empty-message">{{ emptyMessage }}</div>
}
        </ap-list>
      </div>

      <div class="orderlist-controls">
        <ap-button
          [icon]="'up-chevron-outline'"
          [btnType]="'secondary'"
          [disabled]="!hasSelection"
          [ariaLabel]="'Move up'"
          ap-tooltip [appendAfterTarget]="true" tooltipContent="Move up" direction="left"
          (onClick)="moveUp()">
        </ap-button>

        <ap-button
          [icon]="'arrow-up-outline'"
          [btnType]="'secondary'"
          [disabled]="!hasSelection"
          [ariaLabel]="'Move to the top'"
          ap-tooltip [appendAfterTarget]="true" tooltipContent="Move to the top" direction="left"
          (onClick)="moveTop()">
        </ap-button>

        <ap-button
          [icon]="'down-chevron-outline'"
          [btnType]="'secondary'"
          [disabled]="!hasSelection"
          [ariaLabel]="'Move down'"
          ap-tooltip [appendAfterTarget]="true" tooltipContent="Move down" direction="left"
          (onClick)="moveDown()">
        </ap-button>

        <ap-button
          [icon]="'arrow-down-outline'"
          [btnType]="'secondary'"
          [disabled]="!hasSelection"
          [ariaLabel]="'Move to the bottom'"
          ap-tooltip [appendAfterTarget]="true" tooltipContent="Move to the bottom" direction="left"
          (onClick)="moveBottom()">
        </ap-button>
      </div>
    </div>
  </div>

</div>
